/**
 * Cookiebot 工具函数
 */

/**
 * 检查 Cookiebot 同意状态
 * @returns {Object|null} 同意状态对象或 null
 */
export const checkCookiebotConsent = () => {
  if (window.Cookiebot && window.Cookiebot.consent) {
    return {
      statistics: window.Cookiebot.consent.statistics,
      marketing: window.Cookiebot.consent.marketing,
      preferences: window.Cookiebot.consent.preferences,
      necessary: window.Cookiebot.consent.necessary,
    };
  }
  return null;
};

/**
 * 等待 Cookiebot 加载完成
 * @returns {Promise} Cookiebot 对象或 null
 */
export const waitForCookiebot = () => {
  return new Promise((resolve) => {
    if (window.Cookiebot) {
      resolve(window.Cookiebot);
    } else {
      const checkInterval = setInterval(() => {
        if (window.Cookiebot) {
          clearInterval(checkInterval);
          resolve(window.Cookiebot);
        }
      }, 100);

      // 超时处理
      setTimeout(() => {
        clearInterval(checkInterval);
        resolve(null);
      }, 10000);
    }
  });
};

/**
 * 检查是否同意特定类型的 cookies
 * @param {string} type - cookie 类型 ('statistics', 'marketing', 'preferences', 'necessary')
 * @returns {boolean} 是否同意
 */
export const hasConsent = (type) => {
  // 在开发环境中临时绕过Cookiebot检查
  if (process.env.NODE_ENV === 'development') {
    console.log('Development mode: bypassing Cookiebot consent check for', type);
    return true;
  }

  const consent = checkCookiebotConsent();
  return consent ? consent[type] : false;
};

/**
 * 检查是否全部接受 cookies
 * @returns {boolean} 是否全部接受
 */
export const hasAllConsent = () => {
  const consent = checkCookiebotConsent();
  if (!consent) return false;
  return consent.statistics && consent.marketing && consent.preferences && consent.necessary;
};

/**
 * 显示 Cookiebot 设置对话框
 */
export const showCookiebotSettings = () => {
  if (window.Cookiebot) {
    window.Cookiebot.show();
  }
};

/**
 * 隐藏 Cookiebot 横幅
 */
export const hideCookiebotBanner = () => {
  if (window.Cookiebot) {
    window.Cookiebot.hide();
  }
};

/**
 * 监听 Cookiebot 同意状态变化
 * @param {Function} callback - 状态变化时的回调函数
 * @returns {Function} 清理函数
 */
export const onCookiebotConsentChange = (callback) => {
  let cleanup = null;
  let cancelled = false;

  // 等 Cookiebot 就绪后再注册监听，避免脚本异步加载导致监听器注册失败
  waitForCookiebot().then((cookiebot) => {
    if (cancelled) return;
    if (!cookiebot) {
      console.warn('Cookiebot not available');
      return;
    }

    const handleConsentChange = () => {
      const consent = checkCookiebotConsent();
      const allConsent = hasAllConsent();
      callback({
        consent,
        allConsent,
        statistics: consent?.statistics || false,
        marketing: consent?.marketing || false,
        preferences: consent?.preferences || false,
        necessary: consent?.necessary || false,
      });
    };

    // 监听 Cookiebot 的同意状态变化事件
    window.addEventListener('CookiebotOnAccept', handleConsentChange);
    window.addEventListener('CookiebotOnDecline', handleConsentChange);
    window.addEventListener('CookiebotOnLoad', handleConsentChange);

    // 返回清理函数
    cleanup = () => {
      window.removeEventListener('CookiebotOnAccept', handleConsentChange);
      window.removeEventListener('CookiebotOnDecline', handleConsentChange);
      window.removeEventListener('CookiebotOnLoad', handleConsentChange);
    };
  });

  return () => {
    cancelled = true;
    if (cleanup) cleanup();
  };
};

/**
 * 监听 Cookiebot 全部接受事件
 * @param {Function} callback - 全部接受时的回调函数
 * @returns {Function} 清理函数
 */
export const onCookiebotAcceptAll = (callback) => {
  return onCookiebotConsentChange(({ allConsent, consent }) => {
    if (allConsent) {
      callback({
        consent,
        allConsent,
        statistics: consent?.statistics || false,
        marketing: consent?.marketing || false,
        preferences: consent?.preferences || false,
        necessary: consent?.necessary || false,
      });
    }
  });
};

/**
 * 监听 Cookiebot 统计 cookies 同意事件
 * @param {Function} callback - 统计 cookies 同意时的回调函数
 * @returns {Function} 清理函数
 */
export const onCookiebotAcceptStatistics = (callback) => {
  return onCookiebotConsentChange(({ statistics, consent }) => {
    if (statistics) {
      callback({
        consent,
        statistics,
        marketing: consent?.marketing || false,
        preferences: consent?.preferences || false,
        necessary: consent?.necessary || false,
      });
    }
  });
};
